import {
  buildTakeoverReport,
  createIntentRegistry,
  isForeignDestination,
  TAKEOVER_EVENT_NAME,
  MAX_REPORTS_PER_PAGE,
  type TakeoverTrigger,
} from '../utils/ad-ops/adIntegrity'
import { getCookieClient } from '../utils/storage/utils'
import { COHORT_COOKIE } from '../openfeature/utils'
import debug from '../utils/console'

const logger = debug.withTag('plugins:ad-integrity')

const GESTURE_EVENTS = ['pointerdown', 'keydown', 'click', 'auxclick']

/**
 * Ad takeover monitor: detects navigations to foreign origins that no user
 * gesture explains (malvertising forced redirects), cancels them where the
 * platform allows, snapshots GPT slot forensics, and reports each as an
 * NtpAdTakeover_Detected event.
 *
 * Deliberately does NOT treat a bare pagehide as a trigger — tab closes and
 * address-bar navigations are indistinguishable from it and would flood the
 * event.
 */
export default defineNuxtPlugin({
  name: 'ad-integrity',
  dependsOn: ['openfeature'],
  setup: async (nuxtApp) => {
    const { client } = await useOpenFeature()
    if (!client.getBooleanValue('adIntegrityMonitor', false)) {
      return
    }
    const { $filters } = useNuxtApp()
    const { track } = useAnalytics()

    nuxtApp.hook('app:mounted', () => {
      try {
        const registry = createIntentRegistry()
        const pageLoadedAt = Date.now()
        const reportedTriggers = new Set<TakeoverTrigger>()
        let reportsSent = 0

        const report = (
          trigger: TakeoverTrigger,
          destination: string,
          blocked: boolean
        ) => {
          if (reportsSent >= MAX_REPORTS_PER_PAGE) return
          if (reportedTriggers.has(trigger)) return
          reportsSent += 1
          reportedTriggers.add(trigger)

          const takeoverReport = buildTakeoverReport({
            trigger,
            destination,
            blocked,
            city: $filters?.city ?? '',
            country: $filters?.country ?? '',
            splitTestName: client.getStringValue('splittestname', 'control'),
            layoutSkeleton: client.getStringValue('layoutSkeleton', 'control'),
            cohort: getCookieClient(COHORT_COOKIE) ?? 'unknown',
            pageLoadedAt,
            now: Date.now(),
          })
          logger.warn('Untrustworthy navigation detected', trigger, destination)
          void track(TAKEOVER_EVENT_NAME, takeoverReport)
        }

        const recordGesture = (event: Event) => {
          const anchor =
            event.target instanceof Element ? event.target.closest('a') : null
          registry.record({
            ts: Date.now(),
            trusted: event.isTrusted,
            href: anchor?.href ?? null,
          })

          // A script-dispatched click aimed at a foreign origin is the
          // classic forced-click takeover; real user activity nearby is the
          // only thing that excuses it.
          if (
            event.type === 'click' &&
            !event.isTrusted &&
            anchor?.href &&
            isForeignDestination(anchor.href) &&
            !registry.hasRecentTrustedIntent(Date.now())
          ) {
            report('synthetic-click', anchor.href, false)
          }
        }

        for (const type of GESTURE_EVENTS) {
          document.addEventListener(type, recordGesture, {
            capture: true,
            passive: true,
          })
        }

        // A gestureless popup is one nobody asked for — block it and report
        // the destination it was aiming at.
        const nativeOpen = window.open.bind(window)
        window.open = function (
          url?: string | URL,
          target?: string,
          features?: string
        ) {
          const destination = url ? url.toString() : 'about:blank'
          if (!registry.hasRecentTrustedIntent(Date.now())) {
            report('window-open', destination, true)
            return null
          }
          return nativeOpen(url, target, features)
        }

        // Progressive enhancement: where the Navigation API exists it names
        // the destination of a top-level redirect. userInitiated is computed
        // by the browser across frames, so real clicks inside ad iframes
        // (invisible to our listeners) don't false-positive.
        window.navigation?.addEventListener('navigate', (event) => {
          const destination = event.destination?.url
          if (!destination || !isForeignDestination(destination)) return
          if (event.userInitiated) return
          if (registry.hasRecentTrustedIntent(Date.now())) return

          const blocked = event.cancelable
          if (blocked) {
            event.preventDefault()
          }
          report('navigation-api', destination, blocked)
        })
      } catch (error) {
        logger.error('Failed to initialize ad-integrity monitor', error)
      }
    })
  },
})
